import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { Account, AccountErrorKind, AccountServices, LinkEvent } from "../core/account/types";

/** How long restoring the session may take before "Can't reach the sign-in service" (12 FR-82). */
export const SESSION_TIMEOUT_MS = 10_000;
/**
 * How often a signed-in app checks that the account still exists. Supabase can't tell an open
 * app that the owner deleted someone, and their access token stays valid for up to an hour.
 */
export const VERIFY_INTERVAL_MS = 5 * 60_000;

export type AccountState =
  | { status: "checking" }
  | { status: "unreachable" }
  /** `byUser`: signed out on purpose here, rather than the session ending (12 FR-88). */
  | { status: "signed-out"; byUser: boolean }
  | { status: "signed-in"; account: Account };

interface AccountContextValue {
  state: AccountState;
  services: AccountServices;
  /** What the link that opened the app was for, until a screen has handled it. */
  linkEvent: LinkEvent | null;
  clearLinkEvent: () => void;
  /** The error a link brought back on this page load (11 edge cases), until shown once. */
  linkError: AccountErrorKind | null;
  clearLinkError: () => void;
  retry: () => void;
  signOut: (everywhere?: boolean) => Promise<void>;
}

const AccountContext = createContext<AccountContextValue | null>(null);

/** The signed-in person, kept up to date from AccountService.onChange (11 FR-62). */
export function AccountProvider({
  services,
  children,
}: {
  services: AccountServices;
  children: ReactNode;
}) {
  const service = services.account;
  const [state, setState] = useState<AccountState>({ status: "checking" });
  const [linkEvent, setLinkEvent] = useState<LinkEvent | null>(null);
  const [linkError, setLinkError] = useState(() => service.linkError());
  const byUser = useRef(false);

  const show = useCallback((account: Account | null) => {
    if (account) byUser.current = false;
    setState(
      account ? { status: "signed-in", account } : { status: "signed-out", byUser: byUser.current },
    );
  }, []);

  const check = useCallback(() => {
    setState({ status: "checking" });
    const stillChecking = (next: AccountState) => (s: AccountState) =>
      s.status === "checking" ? next : s;
    const timer = setTimeout(
      () => setState(stillChecking({ status: "unreachable" })),
      SESSION_TIMEOUT_MS,
    );
    service.current().then(
      (account) => {
        clearTimeout(timer);
        setState(
          stillChecking(
            account ? { status: "signed-in", account } : { status: "signed-out", byUser: false },
          ),
        );
      },
      () => {
        clearTimeout(timer);
        setState(stillChecking({ status: "unreachable" }));
      },
    );
    return () => clearTimeout(timer);
  }, [service]);

  useEffect(check, [check]);

  // While signed in: check with the server on load, when the tab comes back into view, and
  // every few minutes, so a deleted account is signed out promptly (12 FR-88).
  const signedIn = state.status === "signed-in";
  useEffect(() => {
    if (!signedIn) return;
    const verify = () => {
      service.verify().catch(() => {});
    };
    const onVisible = () => document.visibilityState === "visible" && verify();
    verify();
    const timer = setInterval(verify, VERIFY_INTERVAL_MS);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [signedIn, service]);

  useEffect(
    () =>
      service.onChange((account, event) => {
        show(account);
        if (event) setLinkEvent(event);
      }),
    [service, show],
  );

  const signOut = useCallback(
    async (everywhere = false) => {
      byUser.current = true;
      await service.signOut(everywhere);
    },
    [service],
  );

  const value = useMemo(
    () => ({
      state,
      services,
      linkEvent,
      clearLinkEvent: () => setLinkEvent(null),
      linkError,
      clearLinkError: () => setLinkError(null),
      retry: check,
      signOut,
    }),
    [state, services, linkEvent, linkError, check, signOut],
  );
  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}

export function useAccount() {
  const ctx = useContext(AccountContext);
  if (!ctx) throw new Error("useAccount must be used inside AccountProvider");
  return ctx;
}

/** The signed-in account; only for screens behind RequireAccount. */
export function useSignedInAccount(): Account {
  const { state } = useAccount();
  if (state.status !== "signed-in") throw new Error("Not signed in");
  return state.account;
}
